/**
 * Fill monthly from Ejar - started here, done entirely by the engine.
 *
 *   • The engine reads each contract's Ejar detail, spreads it into the month rows and saves it
 *     (POST /ejar/bundle-job with apply: true) - closing the tab or the browser changes nothing.
 *   • Every session sees the job in progress (who started it, when, how far) through the engine's
 *     /ejar/bundle-job/active, so nobody starts it twice; <EjarFillStatus /> shows it on every page.
 *
 *   ejarFillJob.subscribe(fn) -> unsubscribe      fn({ local, remote })
 *   ejarFillJob.start(contracts, updatedBy)        contracts = contract docs (Monthly page rows)
 *   ejarFillJob.onFinished(fn) -> unregister       e.g. the Monthly page reloads its rows
 */
import { finesService, errorMessage } from './finesService';
import { toast } from '../ui/feedback';

const POLL_MS = 2500;
const RUNNING = ['starting', 'running'];

let local = null;               // the job this tab started: { jobId, total, done, saved, errors[], current, status, cancel }
let remote = null;              // the engine's active job, whoever started it
let last = null;                // the last finished fill: { finishedAt, startedBy, total, saved, unchanged, skipped, ... }
let finished = null;            // a callback of the open Monthly page
let ask = 0;                    // refreshRemote calls: an older answer never overwrites a newer one
let hideTimer = null;
const closed = new Set();       // jobIds whose card was closed (or has ended) - never shown again
const AUTO_HIDE_MS = 8000;      // a clean finish hides itself; one with errors waits for Close
const listeners = new Set();

const keyOf = (c) => String(c || '').trim().toUpperCase();
const snapshot = () => ({ local: local ? { ...local, errors: [...local.errors] } : null, remote, last });
const emit = () => { const s = snapshot(); listeners.forEach((fn) => fn(s)); };

/** Follow the engine's job until it ends (the engine does the saving). */
async function follow(jobId) {
    let after = 0;
    let cancelSent = false;
    try {
        for (;;) {
            if (local.cancel && !cancelSent) {
                cancelSent = true;
                try { await finesService.ejarBundleCancel(jobId); } catch (e) { /* already finished */ }
            }
            const job = await finesService.ejarBundleJob(jobId, after);
            after = job.next;
            Object.assign(local, { current: job.current, total: job.total, done: job.done, saved: job.saved || 0,
                unchanged: job.unchanged || 0, skipped: job.skipped || 0 });
            for (const it of job.items || []) if (it.error) local.errors.push({ contract: it.contract, error: it.error });
            local.status = job.status === 'running' ? 'running' : job.status;
            emit();
            if (job.status !== 'running') break;
            await new Promise((r) => setTimeout(r, POLL_MS));
        }
        toast[local.errors.length ? 'warning' : 'success'](`Fill from Ejar finished: ${local.saved} saved, ${local.errors.length} error(s).`);
        if (finished) { try { await finished(); } catch (e) { /* ignore */ } }
    } catch (e) {
        local.status = 'failed';
        local.errors.push({ contract: '-', error: errorMessage(e) });
        toast.error(`Could not follow the Fill from Ejar: ${errorMessage(e)} - the engine keeps saving; reopen the page to see it.`);
    }
    local.current = null;
    closed.add(jobId);              // ended: an answer still in flight must not bring it back as "running"
    if (remote && remote.jobId === jobId) remote = null;
    emit();
    if (!local.errors.length) {
        clearTimeout(hideTimer);
        const mine = local;
        hideTimer = setTimeout(() => { if (local === mine) ejarFillJob.dismiss(); }, AUTO_HIDE_MS);
    }
    ejarFillJob.refreshRemote();
}

export const ejarFillJob = {
    subscribe(fn) { listeners.add(fn); fn(snapshot()); return () => { listeners.delete(fn); }; },
    get: snapshot,
    /** This tab started one that is still running. */
    isRunning: () => !!local && RUNNING.includes(local.status),

    /** Called once a job this tab follows has ended (the Monthly page reloads its rows). */
    onFinished(fn) {
        finished = fn;
        return () => { if (finished === fn) finished = null; };
    },

    /** Ask the engine what is running (any session). */
    async refreshRemote() {
        const n = ++ask;
        try {
            const r = await finesService.ejarBundleActive();
            if (n !== ask) return remote;                   // a newer answer already came
            const job = r.job || null;
            remote = job && !closed.has(job.jobId) ? job : null;
            last = r.last || last;
        } catch (e) { /* engine unreachable: keep the last */ }
        emit();
        return remote;
    },

    /** Start the fill for these contract docs. Throws (409 names who) when one is running elsewhere. */
    async start(contracts, updatedBy) {
        if (this.isRunning()) return;
        const nos = [...new Set((contracts || []).map((c) => keyOf(c.contractNo || c['Contract No.'])).filter(Boolean))];
        clearTimeout(hideTimer);
        local = { jobId: null, total: nos.length, done: 0, saved: 0, unchanged: 0, skipped: 0, errors: [], current: null, status: 'starting', cancel: false, startedBy: updatedBy };
        emit();
        let jobId;
        try {
            ({ jobId } = await finesService.ejarBundleStart(nos, { apply: true, smart: true }));
        } catch (e) {
            local = null;
            await this.refreshRemote();
            throw e;
        }
        local.jobId = jobId;
        local.status = 'running';
        emit();
        await follow(jobId);
    },

    cancel() { if (local && RUNNING.includes(local.status)) { local.cancel = true; emit(); } },
    /** Close the finished card (and the same job seen from the engine). */
    dismiss() {
        if (!local || RUNNING.includes(local.status)) return;
        if (local.jobId) closed.add(local.jobId);
        if (remote && remote.jobId === local.jobId) remote = null;
        clearTimeout(hideTimer);
        local = null;
        emit();
    },
    /** Hide the card of a job started elsewhere (until the next one). */
    hideRemote(jobId) {
        if (!jobId) return;
        closed.add(jobId);
        if (remote && remote.jobId === jobId) remote = null;
        emit();
    },
};

export default ejarFillJob;
